/**
 * Invoice document hero — `prototype/sales/invoice-detail.html` ▸ `.doc`.
 *
 * Markup follows the prototype verbatim:
 *   `.doc-bar` (toolbar) → `.doc-paper` containing
 *     `.doc-letterhead` (seller + marker)
 *     `.doc-meta-grid` (to + dates)
 *     `.doc-items` (line items table)
 *     `.doc-totals` (note + stack)
 *     `.doc-foot` (notes + .pay)
 *
 * Numeric cells use `<td class="r">` / `<td class="r amount">`. Money
 * values render via `paiseToInrExact` from `lib/format.ts`.
 */
import { initials } from "../../../lib/initials.ts";
import { formatDate, paiseToInrExact, paiseToInrShort } from "../../../lib/format.ts";
import { qtyMilliFormat } from "../lib/format-money.ts";
import { stateNameFromCode } from "../lib/state-codes.ts";
import type { ShimInvoiceTotals } from "../lib/gst-calc-shim.ts";

/** Data needed to render the printable invoice paper. */
export interface InvoiceDocProps {
  invoice: {
    docNumber: string | null;
    issuedOn: string;
    dueOn: string;
    placeOfSupply: string;
    customerNote: string | null;
    poReference: string | null;
    currencyCode: string;
    foreignAmountMinor: string | null;
    fxRateToInrPaise: string | null;
    fxRateDate: string | null;
  };
  company: {
    legalName: string;
    address: string | null;
    city: string | null;
    state: string | null;
    pincode: string | null;
    gstin: string | null;
    bankName: string | null;
    bankAccountNumber: string | null;
    bankIfsc: string | null;
    bankUpiId: string | null;
  };
  customer: {
    name: string;
    gstin: string | null;
    state: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    country: string | null;
    phone: string | null;
  };
  lines: Array<{
    id: string;
    position: number;
    description: string;
    hsn: string;
    qtyMilli: number;
    unit: string;
    ratePaise: string;
    taxRateBps: number;
    lineTotalPaise: string;
    /** ADR-0020 — when present, rendered in place of `ratePaise` in the doc. */
    rateForeignMinor?: string | null;
    /** ADR-0020 — when present, rendered in place of `lineTotalPaise`. */
    lineTotalForeignMinor?: string | null;
  }>;
  /**
   * Per-line credit aggregates — keyed by `sourceInvoiceLineId`. When a
   * line has a matching entry, the document renders strike-through
   * styling on its qty/amount cells and a small "−X credited" note.
   */
  lineCredits?: ReadonlyArray<{
    sourceInvoiceLineId: string;
    qtyMilli: number;
    lineTotalPaise: string;
  }>;
  totals: ShimInvoiceTotals;
  /**
   * Optional reversal stamp. Set when the parent invoice has been fully
   * credit-noted — the document renders a red strip across its header
   * so the cancellation is visible on the paper itself (operators print
   * this; a separate page-level ribbon wouldn't follow the printout).
   */
  reversal?: {
    creditedPaise: bigint;
  };
  /**
   * Sum of credit notes raised against this invoice. When non-zero and
   * the invoice ISN'T fully reversed (handled by `reversal` above), the
   * totals box appends a `Less: credit notes` line and a `Balance owed`
   * running total so the operator sees the partial credit on the paper
   * itself, not just on the rail. Document line items stay verbatim —
   * an issued invoice is append-only — but the bottom math reconciles
   * to what the customer still owes.
   */
  creditedPaise?: bigint;
}

/** Render the invoice document hero with letterhead, items, totals, and footnotes. */
export function InvoiceDoc({
  invoice,
  company,
  customer,
  lines,
  totals,
  reversal,
  creditedPaise = 0n,
  lineCredits = [],
}: InvoiceDocProps) {
  const creditByLineId = new Map(lineCredits.map((c) => [c.sourceInvoiceLineId, c]));
  const docNumber = invoice.docNumber ?? "Draft invoice";
  const taxPaise = totals.cgstPaise + totals.sgstPaise + totals.igstPaise;
  const taxModeLabel = totals.taxMode === "intra" ? "CGST + SGST" : "IGST";
  const hasForeignSnapshot =
    invoice.currencyCode !== "INR" &&
    invoice.foreignAmountMinor !== null &&
    invoice.fxRateToInrPaise !== null;
  const addressLine = [company.address, company.city, company.state, company.pincode]
    .filter(Boolean)
    .join(", ");

  return (
    <article className="doc" aria-label="Printable invoice document">
      <div className="doc-bar">
        <span className="filename">
          <strong>{docNumber}.pdf</strong> · auto-generated
        </span>
      </div>
      <div className={`doc-paper${reversal ? " is-reversed" : ""}`}>
        {reversal ? (
          <div className="doc-reversal-stamp" role="status">
            <span className="doc-reversal-stamp-tag">Reversed</span>
            <span className="doc-reversal-stamp-body">
              This invoice has been fully credited back by credit note.{" "}
              <strong className="num">{paiseToInrShort(reversal.creditedPaise.toString())}</strong>{" "}
              reversed · outstanding balance is zero.
            </span>
          </div>
        ) : null}

        {/* Letterhead */}
        <header className="doc-letterhead">
          <div className="seller">
            <div className="logo" aria-hidden>
              {initials(company.legalName)}
            </div>
            <h2>{company.legalName}</h2>
            <div className="addr">
              {addressLine ? <strong>{addressLine}</strong> : null}
              {company.gstin ? (
                <>
                  <br />
                  GSTIN&nbsp;<span className="num">{company.gstin}</span>
                </>
              ) : (
                <>
                  <br />
                  GSTIN not on file
                </>
              )}
            </div>
          </div>
          <div className="marker">
            <div className="label">Tax Invoice</div>
            <h2>{docNumber}</h2>
            <div className="num-line">
              <span className="k">Issued</span>
              {formatDate(invoice.issuedOn)}
            </div>
            <div className="num-line">
              <span className="k">Due</span>
              {formatDate(invoice.dueOn)}
            </div>
          </div>
        </header>

        {/* Bill-to + dates */}
        <div className="doc-meta-grid">
          <div className="to">
            <div className="lbl">Bill to</div>
            <div className="name">{customer.name}</div>
            <div className="addr">
              {/* Full billing address. Foreign/export buyers show country as the
                  region line; domestic buyers show their Indian state. */}
              {customer.addressLine1 ? <div>{customer.addressLine1}</div> : null}
              {customer.addressLine2 ? <div>{customer.addressLine2}</div> : null}
              {customer.city || customer.country || customer.state ? (
                <div>
                  {[customer.city, customer.country ?? customer.state].filter(Boolean).join(", ") ||
                    "Address not on file"}
                </div>
              ) : (
                <div>Address not on file</div>
              )}
              {customer.phone ? <div>Ph {customer.phone}</div> : null}
              {customer.gstin ? (
                <span className="num">GSTIN {customer.gstin}</span>
              ) : (
                <span className="num">Unregistered counterparty</span>
              )}
            </div>
          </div>
          <div className="dates">
            <div className="cell">
              <div className="k">Issued</div>
              <div className="v">{formatDate(invoice.issuedOn)}</div>
            </div>
            <div className="cell">
              <div className="k">Due</div>
              <div className="v">{formatDate(invoice.dueOn)}</div>
            </div>
            <div className="cell">
              <div className="k">Place of supply</div>
              <div className="v">
                {stateNameFromCode(invoice.placeOfSupply)
                  ? `${stateNameFromCode(invoice.placeOfSupply)} (${invoice.placeOfSupply})`
                  : invoice.placeOfSupply}
              </div>
              <div className="sub">
                {/* Foreign-currency invoices are zero-rated exports (LUT) — never
                    intra/inter-state GST. (ADR-0019/0020.) */}
                {invoice.currencyCode !== "INR"
                  ? "Export · zero-rated (LUT)"
                  : totals.taxMode === "intra"
                    ? "Intra-state · CGST+SGST"
                    : "Inter-state · IGST"}
              </div>
            </div>
            <div className="cell">
              <div className="k">PO reference</div>
              <div className="v">{invoice.poReference ?? "—"}</div>
            </div>
          </div>
        </div>

        {/* Items */}
        <table className="doc-items" aria-label="Line items">
          <thead>
            <tr>
              <th>Description</th>
              <th className="r">HSN</th>
              <th className="r">Qty</th>
              <th className="r">Rate{hasForeignSnapshot ? ` (${invoice.currencyCode})` : ""}</th>
              <th className="r">Tax</th>
              <th className="r">Amount{hasForeignSnapshot ? ` (${invoice.currencyCode})` : ""}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const credit = creditByLineId.get(line.id);
              const fullyCredited = credit !== undefined && credit.qtyMilli >= line.qtyMilli;
              const partiallyCredited =
                credit !== undefined && !fullyCredited && credit.qtyMilli > 0;
              // ADR-0020 — when a line carries foreign-minor values, render
              // them on the doc; INR equivalent appears once in the totals
              // strip below.
              const rateDisplay =
                hasForeignSnapshot && line.rateForeignMinor
                  ? minorToMajor(BigInt(line.rateForeignMinor))
                  : withoutRupee(paiseToInrExact(line.ratePaise));
              const amountDisplay =
                hasForeignSnapshot && line.lineTotalForeignMinor
                  ? minorToMajor(BigInt(line.lineTotalForeignMinor))
                  : withoutRupee(paiseToInrExact(line.lineTotalPaise));
              return (
                <tr
                  key={`${line.position}-${line.description}`}
                  className={
                    fullyCredited ? "is-credited" : partiallyCredited ? "is-partial" : undefined
                  }
                >
                  <td className="desc">
                    <strong>{line.description}</strong>
                    <span className="sub">{line.unit}</span>
                    {credit ? (
                      <span className="line-credit-note">
                        {fullyCredited
                          ? `Returned ${qtyMilliFormat(credit.qtyMilli)} ${line.unit} · −${paiseToInrShort(credit.lineTotalPaise)} credited`
                          : `Returned ${qtyMilliFormat(credit.qtyMilli)} of ${qtyMilliFormat(line.qtyMilli)} ${line.unit} · −${paiseToInrShort(credit.lineTotalPaise)} credited`}
                      </span>
                    ) : null}
                  </td>
                  <td className="r">{line.hsn}</td>
                  <td className="r">
                    <span className={fullyCredited ? "strike" : undefined}>
                      {qtyMilliFormat(line.qtyMilli)} {line.unit}
                    </span>
                  </td>
                  <td className="r">{rateDisplay}</td>
                  <td className="r">{line.taxRateBps / 100}%</td>
                  <td className="r amount">
                    <span className={fullyCredited ? "strike" : undefined}>{amountDisplay}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Totals — note (left) + stack (right) */}
        <div className="doc-totals">
          <div className="note">
            <strong>Tax mode · {taxModeLabel}</strong> —{" "}
            {totals.taxMode === "intra"
              ? "intra-state (supplier & buyer in same state). GST split shown on the right."
              : "inter-state. Single IGST line shown on the right."}
          </div>
          <div className="stack">
            {hasForeignSnapshot ? (
              <>
                {/* Foreign side — customer-facing primary totals. */}
                <div className="row">
                  <span>Invoice total ({invoice.currencyCode})</span>
                  <span className="v">
                    {formatForeign(invoice.currencyCode, BigInt(invoice.foreignAmountMinor!))}
                  </span>
                </div>
                <div className="row grand">
                  <span>Customer pays</span>
                  <span className="v">
                    {formatForeign(invoice.currencyCode, BigInt(invoice.foreignAmountMinor!))}
                  </span>
                </div>
                {/* INR side — ledger book value derived from the booked FX rate. */}
                <div className="row">
                  <span>FX rate</span>
                  <span className="v">
                    ₹{minorToMajor(BigInt(invoice.fxRateToInrPaise!))}/{invoice.currencyCode}
                  </span>
                </div>
                <div className="row">
                  <span>INR book value</span>
                  <span className="v">{paiseToInrExact(totals.grandTotalPaise)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="row">
                  <span>Subtotal · taxable value</span>
                  <span className="v">
                    {withoutRupee(paiseToInrExact(totals.taxableValuePaise))}
                  </span>
                </div>
                <div className="row">
                  <span>{taxModeLabel}</span>
                  <span className="v">{withoutRupee(paiseToInrExact(taxPaise))}</span>
                </div>
                <div className="row grand">
                  <span>Invoice total</span>
                  <span className="v">{paiseToInrExact(totals.grandTotalPaise)}</span>
                </div>
              </>
            )}
            {creditedPaise > 0n && !reversal ? (
              <>
                <div className="row credited">
                  <span>Less: credit notes</span>
                  <span className="v">−{paiseToInrExact(creditedPaise)}</span>
                </div>
                <div className="row balance">
                  <span>Balance owed</span>
                  <span className="v">
                    {paiseToInrExact(totals.grandTotalPaise - creditedPaise)}
                  </span>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Footer — notes left, pay right */}
        <div className="doc-foot">
          <div>
            <strong style={{ color: "var(--ink-2)" }}>Notes to customer.</strong>{" "}
            {invoice.customerNote ??
              "Payment via NEFT / UPI to the account on the right. Mention invoice number in reference."}
          </div>
          <div className="pay">
            <span className="k">Pay to</span> <strong>{company.legalName}</strong>
            {company.bankUpiId ? (
              <>
                <br />
                <span className="k">UPI</span> <strong>{company.bankUpiId}</strong>
              </>
            ) : null}
            <br />
            <span className="k">Bank</span> {company.bankName ?? "—"}
            <br />
            <span className="k">A/c</span> {company.bankAccountNumber ?? "—"}
            <br />
            <span className="k">IFSC</span> {company.bankIfsc ?? "—"}
          </div>
        </div>
      </div>
    </article>
  );
}

function withoutRupee(value: string): string {
  return value.replace("₹", "").trim();
}

function minorToMajor(value: bigint): string {
  const sign = value < 0n ? "-" : "";
  const abs = value < 0n ? -value : value;
  return `${sign}${abs / 100n}.${(abs % 100n).toString().padStart(2, "0")}`;
}

function formatForeign(currencyCode: string, minor: bigint): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
  }).format(Number(minor) / 100);
}
